import {
  MessageSquare, User, Mail, Phone, Building,
  Inbox, Eye, Reply, Download, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ContactStatusBadge } from './StatusBadges';

export const ContactDetailsModal = ({ 
  contact, 
  open, 
  onOpenChange,
  onStatusUpdate,
  onDownload,
  onDelete
}) => {
  if (!contact) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-gold" />
              Contact Message
            </span>
            <ContactStatusBadge status={contact.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Status Actions */}
          <div className="flex items-center gap-2 p-4 bg-steel-50 rounded-lg">
            <span className="text-sm text-steel-600 mr-2">Update Status:</span>
            <Button
              size="sm"
              variant={contact.status === 'new' ? 'default' : 'outline'}
              onClick={() => onStatusUpdate(contact.id, 'new')}
              className="text-xs"
            >
              <Inbox className="w-3 h-3 mr-1" /> New
            </Button>
            <Button
              size="sm"
              variant={contact.status === 'read' ? 'default' : 'outline'}
              onClick={() => onStatusUpdate(contact.id, 'read')}
              className="text-xs"
            >
              <Eye className="w-3 h-3 mr-1" /> Read
            </Button>
            <Button
              size="sm"
              variant={contact.status === 'replied' ? 'default' : 'outline'}
              onClick={() => onStatusUpdate(contact.id, 'replied')}
              className="text-xs bg-green-600 hover:bg-green-700"
            >
              <Reply className="w-3 h-3 mr-1" /> Replied
            </Button>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-navy-800 flex items-center gap-2">
              <User className="w-4 h-4 text-gold" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-steel-400" />
                <span className="text-steel-600">Name:</span>
                <span className="text-navy-800 font-medium">{contact.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-steel-400" />
                <span className="text-steel-600">Email:</span>
                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                  {contact.email}
                </a>
              </div>
              {contact.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-steel-400" />
                  <span className="text-steel-600">Phone:</span>
                  <a href={`tel:${contact.phone}`} className="text-navy-800">
                    {contact.phone}
                  </a>
                </div>
              )}
              {contact.company && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-steel-400" />
                  <span className="text-steel-600">Company:</span>
                  <span className="text-navy-800">{contact.company}</span>
                </div>
              )}
            </div>
          </div>

          {/* Service Interest */}
          {contact.service_interest && (
            <div className="space-y-2">
              <h3 className="font-semibold text-navy-800">Service Interest</h3>
              <Badge variant="outline" className="text-sm capitalize">
                {contact.service_interest}
              </Badge>
            </div>
          )}

          {/* Subject */}
          <div className="space-y-2">
            <h3 className="font-semibold text-navy-800">Subject</h3>
            <p className="text-sm text-navy-800 font-medium bg-steel-50 p-3 rounded-lg">
              {contact.subject}
            </p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <h3 className="font-semibold text-navy-800">Message</h3>
            <div className="text-sm text-steel-700 bg-steel-50 p-4 rounded-lg whitespace-pre-wrap">
              {contact.message}
            </div>
          </div>

          {/* Quick Reply Button */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              className="btn-primary flex-1"
              onClick={() => window.open(`mailto:${contact.email}?subject=Re: ${contact.subject}`, '_blank')}
            >
              <Mail className="w-4 h-4 mr-2" />
              Reply via Email
            </Button>
            {contact.phone && (
              <Button 
                variant="outline"
                onClick={() => window.open(`tel:${contact.phone}`, '_blank')}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call
              </Button>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t pt-4">
            <div className="text-xs text-steel-400">
              <span>Received: {new Date(contact.created_at).toLocaleString()}</span>
              <span className="ml-4">ID: {contact.id}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(contact)}
                className="text-xs"
                data-testid="download-contact-btn"
              >
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onDelete(contact.id)}
                className="text-xs"
                data-testid="delete-contact-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
